/**
 * The wizard's draft: what comes back on a refresh, what is written while the
 * operator works, and what "Pick up where you left off" says.
 *
 * `lib/brandSetup/draft.ts` owns the pure half (what is safe to persist and how
 * to bring it back); this is the React half — one load at mount, a debounced
 * write on every change, and the two resume rules that need the live state.
 */
import { useEffect, useMemo, useState } from "react";
import { hasDraft, loadDraft, saveDraft, serializeDraft } from "../../../lib/brandSetup/draft";
import type { BrandSetupState } from "../../../lib/brandSetup/state";
import { stepNumber, type StepId } from "../../../lib/brandSetup/steps";

export const QUOTA_SENTENCE = "We couldn't save your progress in this browser.";
export const READ_INTERRUPTED_SENTENCE = "Your read was interrupted — read it again.";

export type RestoredDraft = ReturnType<typeof loadDraft>;

/** The draft, read ONCE. Called from the reducer's initializer, so it cannot
 *  be a hook — the state it restores is what the reducer starts from. */
export function readDraftOnce(): RestoredDraft {
  try {
    return loadDraft(localStorage);
  } catch {
    return null;
  }
}

export interface BrandDraftController {
  /** Where "Pick up where you left off" goes, and what it says. */
  resume: { step: StepId; n: number; total: number } | null;
  /** The resume target moved because the read never finished (§2.2). */
  readInterrupted: boolean;
  /** The browser refused to store the draft — a sentence in the rail. */
  quota: boolean;
  /** Stop writing: the brand exists now, the draft is gone. */
  stopSaving: () => void;
}

export function useBrandDraft(args: {
  state: BrandSetupState;
  furthest: StepId;
  restored: RestoredDraft;
}): BrandDraftController {
  const { state, furthest, restored } = args;
  const [quota, setQuota] = useState(false);
  const [off, setOff] = useState(false);

  // What "Pick up where you left off" says and where it goes. `hasDraft` falls
  // FORWARD to the next counted step, so a draft parked on an uncounted screen
  // (welcome, reading) still resumes at a step with a number.
  const [saved] = useState(() => {
    try {
      return hasDraft(localStorage);
    } catch {
      return null;
    }
  });

  /**
   * A draft written WHILE the read was running is a draft with no poller
   * behind it: the study id is saved, the wizard is not listening to it, and
   * nothing will ever tick it forward. `hasDraft` falls forward to `logo`,
   * which resumes into a brand that was never read.
   *
   * So a refresh mid-read goes back to Sources and says why. A read that
   * LANDED (chips on the state) is not interrupted — that draft resumes
   * exactly where it was.
   */
  const readInterrupted = !!restored && restored.furthest === "reading" && restored.state.chips.length === 0;
  const resume = useMemo(() => {
    if (!readInterrupted) return saved;
    const n = stepNumber({ mode: "auto" }, "sources");
    return n ? { step: "sources" as StepId, ...n } : saved;
  }, [readInterrupted, saved]);

  // An UNTOUCHED wizard leaves nothing behind: writing a draft of the initial
  // state would make every entry point read "Continue setup" for someone who
  // has never typed a thing — and would put "Pick up where you left off" on a
  // walk that has not started. Start over therefore also stays cleared.
  const worthSaving =
    state.step !== "welcome" ||
    state.brandName.trim().length > 0 ||
    state.sources.length > 0 ||
    state.mode !== null;

  useEffect(() => {
    if (off || !worthSaving) return;
    const timer = window.setTimeout(() => {
      try {
        setQuota(saveDraft(localStorage, serializeDraft(state, furthest)) === "quota");
      } catch {
        // Storage unavailable (private mode, blocked) — the wizard still works.
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [state, furthest, worthSaving, off]);

  return { resume, readInterrupted, quota, stopSaving: () => setOff(true) };
}
